import { trigger, style, transition, animate } from '@angular/animations';
import { DatePipe, NgClass, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { Router } from '@angular/router';

import { AppService } from '@core/services/app.service';
import { GlobalHandlerService } from '@core/services/global-handler.service';
import { SharedService } from '@core/services/shared.service';
import { NgbPanel, NgbPanelHeader, NgbPanelToggle, NgbPanelContent } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CartItems } from '@shared/classes/my-orders';
import { SlotUI, SubscriptionEventSlots } from '@shared/classes/subscription';
import { ImageLoaderDirective } from '@shared/directives/image-loader.directive';
import { LoadingOverlayComponent } from '@shared/features/loading-overlay/loading-overlay.component';
import { getSafe, isDefined } from '@shared/heplers/helpers';
import { BypassSecurity } from '@shared/pipes/bypassSecurity';
import { LocalizedDatePipe } from '@shared/pipes/localized-date.pipe';
import { MultiplePurchaseService } from '@shared/services/multiple-purchase.service';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { Subscription } from 'rxjs';
import { PreemptionSeatsComponent } from '../../../product-details/preemption/preemption-seats.component';
import { SelectSeatsComponent } from '../../../product-details/select-seats/select-seats.component';
import { SlotDetailsMobileComponent } from '../../../product-details/slot-details-mobile/slot-details-mobile.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '@shared/shared.module';
import { CurvedHeaderComponent } from '@shared/components/curved-header/curved-header.component';
import { SplitDecimalPipe } from '@shared/pipes/decimal-font.pipe';

interface SubscriptionEventSlotsUI extends SubscriptionEventSlots {
  filteredSlots?: Array<SlotUI>;
}

@Component({
  selector: 'app-subscription-ticket-item',
  templateUrl: './subscription-ticket-item.component.html',
  styleUrls: ['./subscription-ticket-item.component.scss'],
  imports: [ImageLoaderDirective, ReactiveFormsModule, SharedModule, NgClass, LoadingOverlayComponent, UpperCasePipe, ImageLoaderDirective, TranslateModule, LocalizedDatePipe, CurvedHeaderComponent, SplitDecimalPipe],
  animations: [
    trigger('slotAnimation', [
      transition(':enter', [
        style({ height: '0px', opacity: 0, overflow: 'hidden', paddingTop: '0', paddingBottom: '0' }),
        animate('300ms ease-in-out', style({ height: '*', opacity: 1, paddingTop: '*', paddingBottom: '*' }))
      ]),
      transition(':leave', [
        style({ height: '*', opacity: 1, overflow: 'hidden' }),
        animate('300ms ease-in-out', style({ height: '0px', opacity: 0, paddingTop: '0', paddingBottom: '0' }))
      ])
    ])
  ]
})
export class SubscriptionTicketItemComponent implements OnInit, OnChanges, OnDestroy {
  private appService = inject(AppService);
  private sharedService = inject(SharedService);
  private multiplePurchaseService = inject(MultiplePurchaseService);
  private translateService = inject(TranslateService);
  private globalHandlerService = inject(GlobalHandlerService);
  private router = inject(Router);
  private fadingOutItems: Set<number> = new Set();

  @Input() supportId: string;
  @Input() subscriptionCartItem: CartItems;
  @Input() isExpanded: boolean = false;
  @Input() multipleCartItem: boolean = false;
  @Output() subscriptionType: string;
  @Output() toggleExpansionOserver: EventEmitter<null> = new EventEmitter();
  @Output() refreshSubscriptionticketsOserver: EventEmitter<null> = new EventEmitter();
  
  transactionDetails: any;
  subEventSlots: SubscriptionEventSlots[] = null;
  filteredSubEventSlots: SubscriptionEventSlotsUI[] = [];
  totalSelectedSlots: number = 0;
  timeoutFilterEvents = null;
  timeoutFilterEventSlots = null;

  usedSlots: number = 0;
  donutChartData: {name: string, value: number}[];
  chartView: [number, number];
  arcWidth: number;
  chartColorScheme = { domain: ['#429adf', '#07ff0b', 'transparent'] };
  disableSelectSlots: boolean = false;
  
  multiplePurchaseLoadingListener: Subscription;
  multiplePurchaseSuccessListener: Subscription;
  cartDeleteEventListener: Subscription;
  showLoading: boolean = false;
  isProceedLoading: boolean = false;
  countLoadingRequests: number = 0;
  isSmallScreen: boolean = window.innerWidth < 600;
  datePipe: DatePipe;

  constructor() {
    this.datePipe = new DatePipe(this.translateService.currentLang)
    this.chartView = this.isSmallScreen ? [100, 100] : [160, 160];
    this.arcWidth = this.isSmallScreen ? 0.5 : 0.167;
  }

  ngOnInit() {
    this.multiplePurchaseLoadingListener = this.multiplePurchaseService.showLoadingObserver.subscribe((loading) => {
      if (!loading) {
        this.isProceedLoading = false;
      }
    })
    this.multiplePurchaseSuccessListener = this.multiplePurchaseService.isMultipleAddToCartSuccess$.subscribe((isSuccess: boolean) => {
      if (this.isExpanded && isSuccess) {
        this.refreshSubscriptionticketsOserver.emit();
        this.resetSelection(false);
        this.router.navigate(['/carrello']);
      }
    });

    this.cartDeleteEventListener = this.sharedService.cartDeleteEventObserver.subscribe(() => {
      if (this.isExpanded) {
        this.refreshSubscriptionticketsOserver.emit();
        this.resetSelection(false);
      }
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes.isExpanded) {
      if (changes.isExpanded.currentValue === true) {
        this.getSubscriptionData();      
      } else {
        this.resetLoadedData();
      }
    }
    if(changes.subscriptionCartItem && changes.subscriptionCartItem.firstChange === false && this.isExpanded) {
      this.setDonutChartData();
    }
  }

  ngOnDestroy(): void {
    this.multiplePurchaseLoadingListener.unsubscribe();
    this.multiplePurchaseSuccessListener.unsubscribe();
    this.cartDeleteEventListener.unsubscribe();
  }

  onToggleExpansion() {
    this.toggleExpansionOserver.emit();
  }

  getSubscriptionData() {
    this.getSubscriptionTransactionsData(this.subscriptionCartItem.cartItemFiscalId);
    this.getSubscriptionSlots(this.subscriptionCartItem.sellableProductId);
  }

  getSubscriptionTransactionsData(cartItemId: number) {
      this.appService.getElement(`siae/transactions/detail?cartItemId=${cartItemId}`).subscribe((response: any) => {
      this.transactionDetails = response;
      this.subscriptionType = response.subscriptionType;
      this.getTransactionSubscriptionTickets();
    }, (error) => {
      this.globalHandlerService.showError(error);
    });
  }

  getTransactionSubscriptionTickets() {
    const params = {
      onlyNotCanceled: true
    }
    this.appService.getAll(`siae/transactions/${this.transactionDetails.resourceId}/subscriptionTickets`, params).subscribe(
      (response: any[]) => {
        this.usedSlots = response.length || 0;
        this.setDonutChartData();
      }, (error) => {
        this.globalHandlerService.showError(error);
      }
    )
  }

  setDonutChartData() {
    if (isDefined(this.usedSlots) && getSafe(() => this.transactionDetails.subscriptionQuantityEnabledEvents)) {
      const numProgressItems = this.subscriptionCartItem.numProgressItems || 0;
      this.donutChartData = [
        { name: "used", value: this.usedSlots + numProgressItems},
        { name: "selected", value: this.totalSelectedSlots},
        { name: "residues", value: (this.transactionDetails.subscriptionQuantityEnabledEvents - this.usedSlots - numProgressItems - this.totalSelectedSlots)}
      ];
      this.disableSelectSlots = this.donutChartData[2].value > 0 ? false : true;
    } else {
      this.disableSelectSlots = false;
    }
  }

  getSubscriptionSlots(subscriptionId: number) {
    const params = {
      onlyFuture: true
    }
    this.appService.getAll(`planning/subscriptions/${subscriptionId}/slots`, params).subscribe((response: SubscriptionEventSlots[]) => {
      this.totalSelectedSlots = 0;
      if (response && response.length) {
        const groupedEventSlots = new Map<string, SubscriptionEventSlots>();

        response.forEach((event: SubscriptionEventSlots) => {
          event.slots.forEach((slot: SlotUI) => {
            slot.formatedDate = this.datePipe.transform(slot.startDateTime, 'EEEE dd MMMM yyyy');
            slot.formatedTime = this.datePipe.transform(slot.startDateTime, 'HH:mm');
            slot.nrSelected = 0;
            slot.eventId = event.eventId;
          });
          const key = `${event.eventProgramTitle}-${event.placeCompleteName}`;
          if (groupedEventSlots.has(key)) {
            groupedEventSlots.get(key).slots.push(...event.slots);
          } else {
            groupedEventSlots.set(key, {...event, slots: [...event.slots]});
          }
        })
        this.subEventSlots = Array.from(groupedEventSlots.values());
        this.filteredSubEventSlots = this.subEventSlots.map((eventSlot: SubscriptionEventSlots) => {
          eventSlot.slots.sort(this.compareByStartDateTime);
          return {...eventSlot, filteredSlots: eventSlot.slots};
        });
      } else {
        this.subEventSlots = [];
        this.filteredSubEventSlots = [];
      }
    }, (error) => {
      this.globalHandlerService.showError(error);
    });
  }

  onFilterEvents(input) {
    clearTimeout(this.timeoutFilterEvents);
    this.timeoutFilterEvents = setTimeout(() => {
      const normalizedSearchInput = isDefined(input.target.value) ? input.target.value.trim().toLowerCase() : null;
      if (normalizedSearchInput) {
        this.filteredSubEventSlots = this.subEventSlots
          .filter((event: SubscriptionEventSlotsUI) => 
            event.eventProgramTitle.toLowerCase().includes(normalizedSearchInput) || 
            event.placeCompleteName.toLowerCase().includes(normalizedSearchInput) ||
            event.slots[0].formatedDate.toLowerCase().includes(normalizedSearchInput)
          ).map((event: SubscriptionEventSlotsUI) => {return {...event, filteredSlots: event.slots}});
      } else {
        this.filteredSubEventSlots = this.subEventSlots.map(
          (eventSlot: SubscriptionEventSlotsUI) => {return {...eventSlot, filteredSlots: eventSlot.slots}}
        );
      }
    }, 500);
  }

  onFilterEventsSlots(input, eventIndex: number) {
    clearTimeout(this.timeoutFilterEventSlots);
    this.timeoutFilterEventSlots = setTimeout(() => {
      const normalizedSearchInput = isDefined(input.target.value) ? input.target.value.trim().toLowerCase() : null;
      if (isDefined(normalizedSearchInput) && isDefined(eventIndex)) {
        this.filteredSubEventSlots[eventIndex].filteredSlots = this.filteredSubEventSlots[eventIndex].slots.filter(
          (slot: SlotUI) => slot.formatedDate.toLowerCase().includes(normalizedSearchInput)
        );
      } else {
        this.filteredSubEventSlots[eventIndex].filteredSlots = this.filteredSubEventSlots[eventIndex].slots;
      }
    }, 500);
  }

  onSetEventSlotNrSelected(slot: SlotUI, operation: string) {
    if (operation === "increase" && this.disableSelectSlots === false) {
      ++slot.nrSelected;
      ++this.totalSelectedSlots;
    } else if (operation === "decrease" && slot.nrSelected > 0) {
      --slot.nrSelected;
      --this.totalSelectedSlots;
    } else if (operation === "cancel") {
      this.totalSelectedSlots = this.totalSelectedSlots - slot.nrSelected;
      slot.nrSelected = 0;
    }
    this.setDonutChartData();
  }

  resetSelection(setChartData: boolean = false) {
    this.subEventSlots.forEach((eventSlot: SubscriptionEventSlots) => {
      eventSlot.slots.forEach((slot: SlotUI) => slot.nrSelected = 0);
    });
    this.totalSelectedSlots = 0;
    if (setChartData === true) {
      this.setDonutChartData();
    }
  }

  resetEventSelection(event: SubscriptionEventSlotsUI) {
    let totalRemovedSelections = 0;
    event.slots.forEach((slot: SlotUI) => {
      totalRemovedSelections += slot.nrSelected;
      slot.nrSelected = 0;
    });
    this.totalSelectedSlots -= totalRemovedSelections;
    this.setDonutChartData();
  }

  onProceed() {
    this.isProceedLoading = true;
    const formatedData = {
      elements: [],
      grouped: false,
      seatsToLock: [],
    }
    this.subEventSlots.forEach((event: SubscriptionEventSlots) => {
      event.slots.forEach((slot: SlotUI) => {
        if (slot.nrSelected > 0) {
          for (let i = 0; i < slot.nrSelected; i++) {
            formatedData.elements.push({
              type: "SUBSCRIPTION_TICKET",
              cartItemId: this.subscriptionCartItem.cartItemFiscalId,
              slotId: slot.resourceId,
              eventId: slot.eventId,
              supportId: this.supportId
            })
          }
        }
      });
    });
    this.multiplePurchaseService.makeMultiplePurchase(formatedData, false);
    
  }

  resetLoadedData() {
    this.transactionDetails = null;
    this.donutChartData = null;
    this.subEventSlots = null;
    this.filteredSubEventSlots = null;
    this.totalSelectedSlots = 0;
  }

  // toggleShowLoading(loading: boolean = false) {
  //   if (loading === true) {
  //     this.countLoadingRequests++;
  //     if (this.countLoadingRequests === 1) {
  //       this.showLoading = true;
  //     }
  //   } else {
  //     this.countLoadingRequests = this.countLoadingRequests > 0 ? this.countLoadingRequests-=1 : 0;
  //     if(this.countLoadingRequests === 0) {
  //       this.showLoading = false;
  //     }
  //   }
  // }

  compareByStartDateTime(a, b) {
    let aStartDateTime = a.startDateTime ? new Date(a.startDateTime) : null;
    let bStartDateTime = b.startDateTime ? new Date(b.startDateTime) : null;
    if (aStartDateTime < bStartDateTime)
      return -1;
    if (aStartDateTime > bStartDateTime)
      return 1;
    return 0;
  }
}

